import { useState, useEffect, useMemo } from 'react';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';
import { fmtDateTimeLong } from '../../utils/dates.js';

// ─── SOW History ─────────────────────────────────────────────────────────────
//
// One record of a job for looking back at it: who was on it and when, then a
// date-ordered feed of everything that happened. Time entries, PLOW and
// planner assignments, changes to the SOW and its milestones, weekly health
// updates and comments all come from GET /sows/:id/history, which withholds
// hours from roles that may not read other people's time; cost is never here.

const KINDS = [
  { key: 'all',     label: 'All' },
  { key: 'time',    label: 'Time logged' },
  { key: 'plow',    label: 'PLOW' },
  { key: 'planner', label: 'Planner' },
  { key: 'change',  label: 'Changes' },
  { key: 'rag',     label: 'Health' },
  { key: 'comment', label: 'Comments' },
];

const FIELD_LABELS = {
  status: 'Status', type: 'Type', commercial_type: 'Commercial Type',
  client_id: 'Client', owner_id: 'Project Manager', client_contact_id: 'Commercial Contact', non_commercial_contact_id: 'Non-commercial Contact',
  delivery_owner_id: 'Delivery Owner', project_manager_id: 'Project Manager', assigned_to_id: 'Assigned To',
  detail: 'Detail', description: 'Description', payment_terms: 'Payment Terms',
  forecast_completion: 'Expected Completion', po_number: 'PO Number', po_received_date: 'PO Received',
  issued_date: 'Issued Date', received_date: 'Date Received', sow_date: 'SOW Date',
  contract_date: 'Contract Date', forecast_date: 'Forecast Date', paid_date: 'Paid Date',
  invoice_number: 'Invoice Number', invoice_amount_excl: 'Invoice Amount (excl VAT)',
  likely_start_date: 'Likely Start', parent_sow_id: 'Parent Opportunity', issued_version: 'Issued Version',
};
const fieldLabel = (f) => FIELD_LABELS[f] || (f || '').replace(/_/g, ' ');
const maybeDate = (v) => (v && /^\d{4}-\d{2}-\d{2}$/.test(v)) ? formatDate(v) : v;
const fmtHours = (h) => (h == null ? '' : `${Math.round(h * 100) / 100}h`);

const DOT = {
  time: 'bg-navy', plow: 'bg-blue-400', planner: 'bg-purple-400',
  change: 'bg-gray-300', comment: 'bg-gold',
};
const RAG_DOT = { red: 'bg-red-500', amber: 'bg-amber-400', green: 'bg-emerald-400' };
const RAG_TEXT = { red: 'text-red-700', amber: 'text-amber-700', green: 'text-emerald-700' };

export default function SowHistoryTab({ sowId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [kind, setKind] = useState('all');
  const [personId, setPersonId] = useState(null);

  useEffect(() => {
    let live = true;
    setData(null); setError(null);
    api.get(`sows/${sowId}/history`)
      .then(d => { if (live) setData(d); })
      .catch(e => { if (live) setError(e?.message || 'Could not load the history'); });
    return () => { live = false; };
  }, [sowId]);

  const counts = useMemo(() => {
    const c = {};
    for (const e of data?.events || []) c[e.kind] = (c[e.kind] || 0) + 1;
    return c;
  }, [data]);

  const groups = useMemo(() => {
    const rows = (data?.events || [])
      .filter(e => kind === 'all' || e.kind === kind)
      .filter(e => !personId || e.person_id === personId);
    const out = [];
    for (const e of rows) {
      const last = out[out.length - 1];
      if (last && last.date === e.date) last.items.push(e);
      else out.push({ date: e.date, items: [e] });
    }
    return out;
  }, [data, kind, personId]);

  if (error) return <div className="text-center py-6 text-[10px] text-red-600">{error}</div>;
  if (!data) return <div className="text-center py-6 text-[10px] text-gray-400">Loading…</div>;

  const { people = [], span = {}, events = [], hours_visible } = data;
  const hasAnything = events.length > 0 || people.length > 0;

  return (
    <div className="space-y-3">
      {/* ── Who and when ── */}
      <div className="bg-white border border-gray-200 rounded p-3">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-[8px] font-bold text-gray-500 uppercase">Who and when</div>
          {span.first_date && (
            <div className="text-[9px] text-gray-500">
              {formatDate(span.first_date)}{span.last_date && span.last_date !== span.first_date ? ` to ${formatDate(span.last_date)}` : ''}
              {' · '}{people.length} {people.length === 1 ? 'person' : 'people'}
            </div>
          )}
        </div>
        {people.length === 0 ? (
          <div className="text-[10px] text-gray-400 py-2">Nobody has logged time or been planned onto this job yet.</div>
        ) : (
          <table className="w-full text-[10px]">
            <thead>
              <tr className="text-[8px] text-gray-400 uppercase text-left">
                <th className="py-1 font-semibold">Person</th>
                <th className="py-1 font-semibold">First day</th>
                <th className="py-1 font-semibold">Last day</th>
                <th className="py-1 font-semibold text-right">Days logged</th>
                <th className="py-1 font-semibold text-right">Days planned</th>
                {hours_visible && <th className="py-1 font-semibold text-right">Hours</th>}
              </tr>
            </thead>
            <tbody>
              {people.map(p => {
                const active = personId === p.person_id;
                return (
                  <tr key={p.person_id}
                      onClick={() => setPersonId(active ? null : p.person_id)}
                      title={active ? 'Show everyone' : `Show only ${p.person_name}`}
                      className={`border-t border-gray-100 cursor-pointer ${active ? 'bg-gold/10' : 'hover:bg-gray-50'}`}>
                    <td className={`py-1 ${active ? 'font-semibold text-navy' : 'text-gray-700'}`}>{p.person_name}</td>
                    <td className="py-1 text-gray-600">{p.first_date ? formatDate(p.first_date) : '—'}</td>
                    <td className="py-1 text-gray-600">{p.last_date ? formatDate(p.last_date) : '—'}</td>
                    <td className="py-1 text-right text-gray-600">{p.logged_days || 0}</td>
                    <td className="py-1 text-right text-gray-600">{p.planned_days || 0}</td>
                    {hours_visible && <td className="py-1 text-right text-gray-600">{fmtHours(p.logged_hours)}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {personId && (
          <div className="mt-2 text-[9px] text-gray-500">
            Showing {people.find(p => p.person_id === personId)?.person_name}.{' '}
            <button className="text-gold hover:underline" onClick={() => setPersonId(null)}>Show everyone</button>
          </div>
        )}
      </div>

      {/* ── Feed ── */}
      <div className="bg-white border border-gray-200 rounded p-3">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="text-[8px] font-bold text-gray-500 uppercase">History</div>
          <div className="flex gap-1 flex-wrap">
            {KINDS.map(k => {
              const n = k.key === 'all' ? events.length : (counts[k.key] || 0);
              return (
                <button key={k.key} onClick={() => setKind(k.key)}
                  className={`px-2 py-0.5 text-[8px] rounded font-semibold ${kind === k.key ? 'bg-navy text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                  {k.label}{n ? ` (${n})` : ''}
                </button>
              );
            })}
          </div>
        </div>

        {!hasAnything && <div className="text-center py-4 text-[10px] text-gray-400">No history yet for this job.</div>}
        {hasAnything && groups.length === 0 && <div className="text-center py-4 text-[10px] text-gray-400">No history of that kind{personId ? ' for that person' : ''}.</div>}

        <div className="space-y-4">
          {groups.map(({ date, items }) => (
            <div key={date || 'undated'}>
              <div className="flex items-center gap-2 mb-1.5">
                <div className="flex-1 h-px bg-gray-100" />
                <div className="text-[8px] font-semibold text-gray-500 whitespace-nowrap">{date ? formatDate(date) : 'Undated'}</div>
                <div className="flex-1 h-px bg-gray-100" />
              </div>
              <div className="space-y-1">
                {items.map(e => <HistoryItem key={`${e.kind}-${e.id}`} item={e} />)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function HistoryItem({ item }) {
  const dot = item.kind === 'rag' ? (RAG_DOT[item.rag] || 'bg-gray-300') : (DOT[item.kind] || 'bg-gray-300');
  return (
    <div className="flex gap-2">
      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${dot}`} />
      <div className="flex-1 min-w-0 text-[10px] text-gray-700">
        <HistoryText item={item} />
      </div>
    </div>
  );
}

function HistoryText({ item }) {
  switch (item.kind) {
    case 'time':
      return (
        <div>
          <span className="font-semibold text-navy">{item.person_name}</span>
          {' '}logged time{item.hours != null ? <> · <span className="font-semibold">{fmtHours(item.hours)}</span></> : null}
          {item.milestone_code ? <span className="text-gray-500"> · {item.milestone_code}</span> : null}
          {item.detail ? <span className="text-gray-500"> · {item.detail}</span> : null}
        </div>
      );
    case 'plow':
      return (
        <div>
          <span className="font-semibold text-navy">{item.person_name}</span>
          {' '}on the <span className="font-medium">{item.plow_name}</span> PLOW
          {item.sections?.length ? <span className="text-gray-500"> ({item.sections.join(', ')})</span> : null}
          {item.detail ? <span className="text-gray-500"> · {item.detail}</span> : null}
          {item.note ? <span className="text-gray-500 italic"> · {item.note}</span> : null}
        </div>
      );
    case 'planner':
      return (
        <div>
          <span className="font-semibold text-navy">{item.person_name}</span>
          {' '}planned{item.end_date ? <> to <span className="font-medium">{formatDate(item.end_date)}</span></> : null}
          {item.slot_type && item.slot_type !== 'full_day' ? <span className="text-gray-500"> ({item.slot_type.toUpperCase()})</span> : null}
          {item.detail ? <span className="text-gray-500"> · {item.detail}</span> : null}
        </div>
      );
    case 'change': {
      const created = item.action === 'created' || !item.field_name;
      return (
        <div>
          <span className="text-[8px] text-gray-400">{fmtDateTimeLong(item.ts)}</span>
          {item.actor && <span className="text-[8px] text-gray-500"> · {item.actor}</span>}
          <span className="ml-1 text-[9px] text-gray-600">
            {created ? (
              <span className="font-semibold text-emerald-600">{item.milestone_code ? `Milestone ${item.milestone_code} created` : 'Opportunity created'}</span>
            ) : (
              <>
                Changed <span className="font-semibold text-navy">{fieldLabel(item.field_name)}</span>
                {item.milestone_code ? <> on <span className="font-medium">{item.milestone_code}</span></> : null}
                {/* A change to a SOW money field comes without its values for anybody not shown SOW money. */}
                {(item.old_value !== undefined || item.new_value !== undefined) && <>
                {' '}from <span className="line-through text-gray-400">{item.old_value ? maybeDate(item.old_value) : '(empty)'}</span>
                {' '}to <span className="font-semibold">{item.new_value ? maybeDate(item.new_value) : '(empty)'}</span>
                </>}
              </>
            )}
          </span>
        </div>
      );
    }
    case 'rag':
      return (
        <div>
          <span className="font-semibold text-navy">Health update</span>
          {item.rag ? <span className={`ml-1 font-semibold uppercase text-[8px] ${RAG_TEXT[item.rag] || 'text-gray-500'}`}>{item.rag}</span> : null}
          {item.milestone_code ? <span className="text-gray-500"> · {item.milestone_code}</span> : null}
          {item.actor ? <span className="text-gray-500"> · {item.actor}</span> : null}
          {item.detail ? <div className="text-gray-600 mt-0.5 whitespace-pre-wrap">{item.detail}</div> : null}
        </div>
      );
    case 'comment':
      return (
        <div className="bg-gold/5 border border-gold/20 rounded-r px-2 py-1.5">
          <div className="text-[8px] text-gray-400">{fmtDateTimeLong(item.ts)} · <span className="font-semibold">{item.actor}</span></div>
          <div className="text-gray-700 mt-0.5 whitespace-pre-wrap">{item.detail}</div>
        </div>
      );
    default:
      return <div>{item.detail}</div>;
  }
}
